/**
 * InvestmentAnalysis — NPV analysis display for switching heating systems
 */

import {
  Paper,
  Stack,
  Text,
  Group,
  Badge,
  SimpleGrid,
  Card,
} from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import type { InvestmentAnalysisResult } from '../types/heatingCost.types';
import { FUEL_VARIANTS } from '../constants/fuelDefaults';

interface InvestmentAnalysisProps {
  analysis: InvestmentAnalysisResult;
}

const RECOMMENDATION_COLORS: Record<string, string> = {
  highly_recommended: 'green',
  recommended: 'teal',
  neutral: 'yellow',
  not_recommended: 'red',
};

const RECOMMENDATION_LABELS: Record<string, string> = {
  highly_recommended: 'Zdecydowanie polecane',
  recommended: 'Polecane',
  neutral: 'Neutralne',
  not_recommended: 'Nie polecane',
};

function formatCurrency(value: number): string {
  return value.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function InvestmentAnalysis({ analysis }: InvestmentAnalysisProps) {
  const { npvAnalysis, recommendation, recommendationReason } = analysis;
  const fromVariant = FUEL_VARIANTS[analysis.fromVariant];
  const toVariant = FUEL_VARIANTS[analysis.toVariant];

  const chartData = npvAnalysis.annualCashFlows.map((value, index) => ({
    rok: index + 1,
    'Skumulowane oszczędności': Math.round(value),
  }));

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <Text size="sm" fw={500}>
            Analiza inwestycji: {fromVariant.label} ({fromVariant.boilerType}) → {toVariant.label} ({toVariant.boilerType})
          </Text>
          <Badge
            color={RECOMMENDATION_COLORS[recommendation]}
            size="lg"
            variant="light"
          >
            {RECOMMENDATION_LABELS[recommendation]}
          </Badge>
        </Group>

        <Text size="xs" c="dimmed">{recommendationReason}</Text>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
          <Card withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed">Koszt instalacji</Text>
            <Text size="sm" fw={600}>{formatCurrency(npvAnalysis.investmentCost)} zł</Text>
          </Card>
          <Card withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed">Roczne oszczędności</Text>
            <Text size="sm" fw={600} c={npvAnalysis.annualSavings > 0 ? 'green' : 'red'}>
              {formatCurrency(npvAnalysis.annualSavings)} zł
            </Text>
          </Card>
          <Card withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed">Okres zwrotu</Text>
            <Text size="sm" fw={600}>
              {npvAnalysis.simplePaybackYears === Infinity
                ? 'Brak zwrotu'
                : `${npvAnalysis.simplePaybackYears.toFixed(1)} lat`}
            </Text>
          </Card>
          <Card withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed">NPV (15 lat)</Text>
            <Text size="sm" fw={600} c={npvAnalysis.npv15Year > 0 ? 'green' : 'red'}>
              {formatCurrency(npvAnalysis.npv15Year)} zł
            </Text>
          </Card>
        </SimpleGrid>

        {npvAnalysis.annualCashFlows.length > 0 && (
          <AreaChart
            h={200}
            data={chartData}
            dataKey="rok"
            series={[{ name: 'Skumulowane oszczędności', color: 'teal.6' }]}
            curveType="natural"
            gridAxis="xy"
            valueFormatter={(value) => `${formatCurrency(value)} zł`}
            referenceLines={[{ y: 0, color: 'red.5', label: 'Punkt zwrotu' }]}
          />
        )}

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
          <Text size="xs" c="dimmed">
            NPV 10 lat: <strong>{formatCurrency(npvAnalysis.npv10Year)} zł</strong>
          </Text>
          <Text size="xs" c="dimmed">
            NPV 20 lat: <strong>{formatCurrency(npvAnalysis.npv20Year)} zł</strong>
          </Text>
          <Text size="xs" c="dimmed">
            Punkt zwrotu: <strong>
              {npvAnalysis.breakEvenYear
                ? `rok ${npvAnalysis.breakEvenYear}`
                : 'brak w okresie analizy'}
            </strong>
          </Text>
        </SimpleGrid>
      </Stack>
    </Paper>
  );
}
